import React, { useEffect, useState } from "react";
import { Loader2, Users } from "lucide-react";
import Modal from "../../../components/Modal";
import {
  controleProducaoService,
  FichaDiariaUsuarioSetor,
} from "../../../services/controleProducaoService";

export type EquipeSetorAlvo = {
  setorId: number;
  setorNome: string;
};

type BotaoEquipeSetorProps = {
  onClick: () => void;
  compact?: boolean;
  label?: string;
};

export const BotaoEquipeSetor: React.FC<BotaoEquipeSetorProps> = ({
  onClick,
  compact,
  label = "Equipe",
}) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    aria-label="Equipe do setor"
    title="Ver usuários ativos do setor"
    className={
      compact
        ? "inline-flex h-8 items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-2.5 text-[11px] font-semibold text-primary touch-manipulation hover:bg-primary/5"
        : "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-primary/20 bg-white px-3 text-sm font-semibold text-primary touch-manipulation hover:bg-primary/5"
    }
  >
    <Users className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} aria-hidden />
    {label}
  </button>
);

type FichaDiariaEquipeSetorModalProps = {
  setor: EquipeSetorAlvo | null;
  lojaId?: number;
  onClose: () => void;
};

const FichaDiariaEquipeSetorModal: React.FC<
  FichaDiariaEquipeSetorModalProps
> = ({ setor, lojaId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [usuarios, setUsuarios] = useState<FichaDiariaUsuarioSetor[]>([]);
  const [setorNome, setSetorNome] = useState(setor?.setorNome ?? "");

  useEffect(() => {
    if (!setor) {
      setUsuarios([]);
      setErro(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErro(null);
    setSetorNome(setor.setorNome);
    void controleProducaoService
      .getUsuariosSetorFicha({ setorId: setor.setorId, lojaId })
      .then((data) => {
        if (cancelled) return;
        setSetorNome(data.setorNome || setor.setorNome);
        setUsuarios(data.usuarios);
      })
      .catch((error: { message?: string }) => {
        if (cancelled) return;
        setErro(error?.message || "Não foi possível carregar a equipe.");
        setUsuarios([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setor, lojaId]);

  return (
    <Modal
      isOpen={setor != null}
      onClose={onClose}
      title="Equipe do setor"
      description={setorNome || undefined}
      panelClassName="max-w-lg"
      bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6"
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-600">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Carregando equipe…
        </div>
      ) : erro ? (
        <p className="py-6 text-center text-sm text-red-600">{erro}</p>
      ) : usuarios.length === 0 ? (
        <p className="py-8 text-center text-sm text-neutral-500">
          Nenhum usuário ativo vinculado a este setor.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {usuarios.map((usuario) => (
            <li
              key={usuario.id}
              className="flex items-start justify-between gap-3 py-3"
            >
              <p className="min-w-0 font-medium text-neutral-900">
                {usuario.nome}
              </p>
              <span className="shrink-0 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-700">
                {usuario.funcao}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};

export default FichaDiariaEquipeSetorModal;
